import React, { useState, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import { getAuthors, lookupOrcid } from '../api/client';

/**
 * AuthorPickerModal — reusable author selection dialog.
 *
 * Three tabs:
 *  1. "ORCID" — paste a 16-digit ORCID iD, backend looks it up (local DB
 *     first, then ORCID.org public record). If found locally, returns the
 *     existing author. Otherwise pre-fills a "new author" form.
 *  2. "Search" — filter existing DB authors by name/email/ORCID.
 *  3. "Manual" — type a fresh author from scratch.
 *
 * On save, the parent receives ``{ mode: 'existing', id }`` or
 * ``{ mode: 'new', author: {...} }`` via ``onSelect``.
 *
 * ``role`` is passed back unchanged (default ``"author"``) so callers can
 * reuse this modal for co-authors too.
 */

const emptyNewAuthor = {
  first_name: '', last_name: '', middle_name: '', email: '', phone: '',
  orcid: '', country: '', affiliation: '',
  first_name_en: '', last_name_en: '', affiliation_en: '',
  first_name_ru: '', last_name_ru: '', affiliation_ru: '',
  language: 'uz',
};

const inputCls =
  'w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none';

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-slate-500 mb-1">
        {label}{required ? <span className="text-rose-500"> *</span> : null}
      </span>
      {children}
    </label>
  );
}

export default function AuthorPickerModal({ isOpen, onClose, onSelect, role = 'author', existingAuthors: preloaded }) {
  const [tab, setTab] = useState('orcid');
  const [authors, setAuthors] = useState(preloaded || []);
  const [loadingAuthors, setLoadingAuthors] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [orcidInput, setOrcidInput] = useState('');
  const [orcidLoading, setOrcidLoading] = useState(false);
  const [orcidResult, setOrcidResult] = useState(null);
  const [newAuthor, setNewAuthor] = useState(emptyNewAuthor);

  useEffect(() => {
    if (!isOpen) return;
    setTab('orcid');
    setOrcidInput('');
    setOrcidResult(null);
    setSearch('');
    setSelectedId('');
    setNewAuthor(emptyNewAuthor);
    if (preloaded && preloaded.length) return;
    setLoadingAuthors(true);
    getAuthors()
      .then(res => setAuthors(Array.isArray(res.data) ? res.data : []))
      .catch(() => toast.error("Mualliflar ro'yxatini olib bo'lmadi"))
      .finally(() => setLoadingAuthors(false));
  }, [isOpen, preloaded]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return authors.slice(0, 50);
    return authors.filter(a => {
      const hay = `${a.last_name || ''} ${a.first_name || ''} ${a.email || ''} ${a.orcid || ''}`.toLowerCase();
      return hay.includes(q);
    }).slice(0, 50);
  }, [authors, search]);

  if (!isOpen) return null;

  const handleOrcidLookup = async () => {
    const trimmed = orcidInput.trim();
    if (!/^\d{4}-\d{4}-\d{4}-\d{3}[\dX]$/.test(trimmed)) {
      toast.error("ORCID formati: 0000-0000-0000-0000");
      return;
    }
    setOrcidLoading(true);
    setOrcidResult(null);
    try {
      const res = await lookupOrcid(trimmed);
      setOrcidResult(res.data);
      if (res.data.found_local) {
        toast.success("Bu ORCID bilan muallif bazada mavjud");
      } else if (res.data.orcid_profile) {
        toast.success("ORCID.org dan ma'lumot olindi");
      } else {
        toast("ORCID topilmadi — qo'lda to'ldirishingiz mumkin", { icon: "ℹ️" });
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "ORCID izlashda xato";
      toast.error(msg);
    } finally {
      setOrcidLoading(false);
    }
  };

  const handleUseOrcidProfile = () => {
    if (!orcidResult) return;
    if (orcidResult.found_local && orcidResult.local_author) {
      onSelect({ mode: 'existing', id: orcidResult.local_author.id, author: orcidResult.local_author, role });
      onClose();
      return;
    }
    const p = orcidResult.orcid_profile;
    if (!p) return;
    setNewAuthor({
      ...emptyNewAuthor,
      orcid: p.orcid || orcidInput,
      first_name: p.first_name || '',
      last_name: p.last_name || '',
      email: p.email || '',
      affiliation: p.affiliation || '',
    });
    setTab('manual');
    toast("ORCID ma'lumotlari tayyorlandi — ko'rib chiqing va saqlang", { icon: "📝" });
  };

  const handleSelectExisting = () => {
    if (!selectedId) {
      toast.error("Muallifni tanlang");
      return;
    }
    const a = authors.find(x => String(x.id) === String(selectedId));
    onSelect({ mode: 'existing', id: parseInt(selectedId, 10), author: a, role });
    onClose();
  };

  const handleSaveNew = () => {
    if (!newAuthor.first_name || !newAuthor.last_name || !newAuthor.email || !newAuthor.affiliation) {
      toast.error("Ism, familiya, email va affiliation majburiy");
      return;
    }
    onSelect({ mode: 'new', author: newAuthor, role });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center p-4 overflow-y-auto">
      <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl my-8 border border-slate-200 dark:border-slate-700">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-700">
          <h3 className="text-base font-semibold">
            {role === 'coauthor' ? "Hammuallif qo'shish" : "Muallifni qo'shish"}
          </h3>
          <button onClick={onClose} className="p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-100 dark:border-slate-700 px-6">
          {[
            { id: 'orcid', label: '🔍 ORCID orqali' },
            { id: 'search', label: '👥 Mavjud mualliflar' },
            { id: 'manual', label: '✍️ Qo\'lda kiritish' },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={
                'px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ' +
                (tab === t.id
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-slate-500 hover:text-slate-700')
              }
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="p-6">
          {/* ── ORCID tab ── */}
          {tab === 'orcid' && (
            <div className="space-y-4">
              <div>
                <Field label="ORCID iD">
                  <div className="flex gap-2">
                    <input
                      value={orcidInput}
                      onChange={e => setOrcidInput(e.target.value)}
                      placeholder="0000-0000-0000-0000"
                      className={inputCls}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleOrcidLookup(); } }}
                    />
                    <button
                      type="button"
                      onClick={handleOrcidLookup}
                      disabled={orcidLoading}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium whitespace-nowrap"
                    >
                      {orcidLoading ? 'Izlanmoqda…' : 'Izlash'}
                    </button>
                  </div>
                </Field>
                <p className="text-xs text-slate-500 mt-2">
                  ORCID.org va mahalliy bazadan muallif ma'lumotlari avtomatik olinadi.
                </p>
              </div>

              {orcidResult && orcidResult.found_local && orcidResult.local_author && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700 mb-2">
                    ✓ Mahalliy bazada topildi
                  </p>
                  <p className="text-sm">
                    <strong>{orcidResult.local_author.last_name} {orcidResult.local_author.first_name}</strong>
                    {orcidResult.local_author.middle_name && ` ${orcidResult.local_author.middle_name}`}
                  </p>
                  <p className="text-xs text-slate-600">{orcidResult.local_author.email}</p>
                  {orcidResult.local_author.affiliation && (
                    <p className="text-xs text-slate-600">{orcidResult.local_author.affiliation}</p>
                  )}
                  <button
                    type="button"
                    onClick={handleUseOrcidProfile}
                    className="mt-3 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium"
                  >
                    Ushbu muallifni tanlash
                  </button>
                </div>
              )}

              {orcidResult && !orcidResult.found_local && orcidResult.orcid_profile && (
                <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-blue-700 mb-2">
                    ORCID.org dan topildi
                  </p>
                  <p className="text-sm">
                    <strong>{orcidResult.orcid_profile.last_name} {orcidResult.orcid_profile.first_name}</strong>
                  </p>
                  {orcidResult.orcid_profile.email && (
                    <p className="text-xs text-slate-600">{orcidResult.orcid_profile.email}</p>
                  )}
                  {orcidResult.orcid_profile.affiliation && (
                    <p className="text-xs text-slate-600">{orcidResult.orcid_profile.affiliation}</p>
                  )}
                  <button
                    type="button"
                    onClick={handleUseOrcidProfile}
                    className="mt-3 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium"
                  >
                    Ma'lumotlarni yangi muallif formasiga o'tkazish
                  </button>
                </div>
              )}

              {orcidResult && !orcidResult.found_local && !orcidResult.orcid_profile && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                  ORCID bo'yicha ma'lumot topilmadi. <button type="button" onClick={() => { setNewAuthor({ ...emptyNewAuthor, orcid: orcidInput }); setTab('manual'); }} className="underline font-medium">Qo'lda kiritishga o'tish</button>
                </div>
              )}
            </div>
          )}

          {/* ── Search existing ── */}
          {tab === 'search' && (
            <div className="space-y-3">
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Ism, familiya, email yoki ORCID bo'yicha qidirish…"
                className={inputCls}
              />
              {loadingAuthors ? (
                <p className="text-sm text-slate-500">Yuklanmoqda…</p>
              ) : (
                <>
                  <select
                    size={8}
                    value={selectedId}
                    onChange={e => setSelectedId(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                  >
                    <option value="">— Muallifni tanlang —</option>
                    {filtered.map(a => (
                      <option key={a.id} value={a.id}>
                        {a.last_name} {a.first_name}
                        {a.middle_name ? ` ${a.middle_name}` : ''} — {a.email}
                        {a.affiliation ? ` (${a.affiliation})` : ''}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-slate-500">
                    {authors.length} ta muallif mavjud · {filtered.length} ta ko'rsatilmoqda
                  </p>
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={handleSelectExisting}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium"
                    >
                      Tanlangan muallifni qo'shish
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── Manual entry ── */}
          {tab === 'manual' && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <Field label="Ism" required>
                  <input value={newAuthor.first_name} onChange={e => setNewAuthor(p => ({ ...p, first_name: e.target.value }))} className={inputCls} />
                </Field>
                <Field label="Familiya" required>
                  <input value={newAuthor.last_name} onChange={e => setNewAuthor(p => ({ ...p, last_name: e.target.value }))} className={inputCls} />
                </Field>
                <Field label="Otasining ismi">
                  <input value={newAuthor.middle_name} onChange={e => setNewAuthor(p => ({ ...p, middle_name: e.target.value }))} className={inputCls} />
                </Field>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Field label="First name (EN)">
                  <input value={newAuthor.first_name_en} onChange={e => setNewAuthor(p => ({ ...p, first_name_en: e.target.value }))} className={inputCls} />
                </Field>
                <Field label="Last name (EN)">
                  <input value={newAuthor.last_name_en} onChange={e => setNewAuthor(p => ({ ...p, last_name_en: e.target.value }))} className={inputCls} />
                </Field>
                <Field label="Tel.">
                  <input value={newAuthor.phone} onChange={e => setNewAuthor(p => ({ ...p, phone: e.target.value }))} className={inputCls} />
                </Field>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Email" required>
                  <input type="email" value={newAuthor.email} onChange={e => setNewAuthor(p => ({ ...p, email: e.target.value }))} className={inputCls} />
                </Field>
                <Field label="ORCID">
                  <input value={newAuthor.orcid} onChange={e => setNewAuthor(p => ({ ...p, orcid: e.target.value }))} placeholder="0000-0000-0000-0000" className={inputCls} />
                </Field>
                <Field label="Davlat (ISO-2)">
                  <input value={newAuthor.country} onChange={e => setNewAuthor(p => ({ ...p, country: e.target.value }))} placeholder="UZ" maxLength={10} className={inputCls} />
                </Field>
              </div>
              <Field label="Lavozim / muassasa (UZ)" required>
                <input value={newAuthor.affiliation} onChange={e => setNewAuthor(p => ({ ...p, affiliation: e.target.value }))} className={inputCls} />
              </Field>
              <Field label="Affiliation (EN)">
                <input value={newAuthor.affiliation_en} onChange={e => setNewAuthor(p => ({ ...p, affiliation_en: e.target.value }))} className={inputCls} />
              </Field>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleSaveNew}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium"
                >
                  Qo'shish
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
